import { useCallback, useEffect, useMemo, useState } from 'react'
import { useReactiveVar } from '@apollo/client'

import { currentUserVar, notificationMessageVar } from 'modulesCommon/store'
import { getBrowser } from 'shared/utils/getUserSystemData'
import { TBrowserNotificationsData } from 'modulesCommon/Notifications/types'
import {
  getPushNotificationSupported,
  registerServiceWorker,
  getUserSubscription,
  askUserPermission,
  createNotificationSubscription
} from 'modulesCommon/Notifications/utils/browserNotifications'
import { useSubscribeToPushNotifications, useUnsubscribeToPushNotifications } from 'modulesCommon/graphql/mutations'
import { useLoading } from 'shared/hooks'

const useBrowserNotifications = () => {
  const { name: browserName } = getBrowser()

  const [userConsent, setUserConsent] = useState(Notification.permission)
  const [userSubscription, setUserSubscription] = useState<any>(null)

  const currentUser = useReactiveVar(currentUserVar)

  const { mutate: subscribeToPushNotifications, loading: subscribeToPushNotificationsLoading } =
    useSubscribeToPushNotifications()
  const { mutate: unsubscribeToPushNotifications, loading: unsubscribeToPushNotificationsLoading } =
    useUnsubscribeToPushNotifications()

  const isPushNotificationSupported = useMemo(() => getPushNotificationSupported(), [])

  const hasSubscription = isPushNotificationSupported && userConsent === 'granted' && userSubscription

  const notificationsData = useMemo<TBrowserNotificationsData[]>(
    () => [{ status: hasSubscription, name: browserName, userId: currentUser?.id }],
    [browserName, hasSubscription, currentUser?.id]
  )

  const onAskUserPermission = useCallback(async () => {
    const consent = await askUserPermission()

    if (consent !== 'granted') {
      notificationMessageVar({
        type: 'warning',
        title: 'Warning',
        message: 'You denied the consent to receive notifications'
      })
      return
    }

    setUserConsent(consent)
    return consent
  }, [])

  const onSubscribe = useCallback(
    async (user_id: string) => {
      const consent = await onAskUserPermission()

      if (consent) {
        const subscription = await createNotificationSubscription()

        if (subscription) {
          const { data } = await subscribeToPushNotifications({
            variables: { user_id, subscription_info: JSON.stringify(subscription) }
          })

          if (data.subscribeToPushNotifications) {
            setUserSubscription(subscription)
            notificationMessageVar({
              type: 'success',
              title: 'Success',
              message: 'You have successfully subscribe Browser Notifications.'
            })
          }
        }
      }
    },
    [onAskUserPermission, subscribeToPushNotifications]
  )

  const onUnsubscribe = useCallback(
    async (user_id: string) => {
      const subscription = await getUserSubscription()

      if (subscription) {
        await subscription.unsubscribe()
        setUserSubscription(null)
        await unsubscribeToPushNotifications({
          variables: { user_id }
        })
        notificationMessageVar({
          type: 'success',
          title: 'Success',
          message: 'You have successfully unsubscribe Browser Notifications.'
        })
      }
    },
    [unsubscribeToPushNotifications]
  )

  const toggleSubscription = useCallback(
    (e: any, id: string) => {
      if (isPushNotificationSupported) {
        const action = e.target.checked ? onSubscribe : onUnsubscribe
        action(id)
      } else {
        notificationMessageVar({
          type: 'danger',
          title: 'Error',
          message: 'Browser does not support desktop notification'
        })
      }
    },
    [isPushNotificationSupported, onSubscribe, onUnsubscribe]
  )

  useEffect(() => {
    if (isPushNotificationSupported) {
      const getRegisterServiceWorker = async () => {
        await registerServiceWorker()
      }
      getRegisterServiceWorker()
    }
  }, [isPushNotificationSupported])

  useEffect(() => {
    const getExistingSubscription = async () => {
      const existingSubscription = await getUserSubscription()
      setUserSubscription(existingSubscription)
    }
    getExistingSubscription()
  }, [])

  useLoading({ isLoading: unsubscribeToPushNotificationsLoading || subscribeToPushNotificationsLoading })

  return {
    notificationsData,
    toggleSubscription
  }
}

export default useBrowserNotifications
